import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

export type CountryType = "blocked" | "white";

const types: CountryType[] = ["white", "blocked"];

export interface CountryItem {
  code: string;
  name: string;
}

export interface CountriesState {
  list: CountryItem[];
  whiteList: CountryItem[];
  blockedList: CountryItem[];
  selectedType: CountryType;
  types: CountryType[];
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  isDomainCountriesFetching: boolean;
  isDomainsFetching: boolean;
  domainCountryCodes: CountryItem["code"][];
  domainCountriesDomain: string;
  availableDomains: string[];
  selectedDomain: string;
  activePage: number;
  totalItems: number;
  totalPages: number;
  draftItem?: Partial<CountryItem>;
  processingItemId?: CountryItem["code"];
}

interface GetCountriesListPayload {
  domain: string;
  limit?: number;
  offset?: number;
  type: CountryType;
}

interface UpdateCountriesPayload {
  domain: string;
  type: CountryType;
  countries: CountryItem["code"][];
}

interface DeleteCountryPayload {
  domain?: string;
  type: CountryType;
  countries: CountryItem["code"][];
}

interface CountriesListResponse {
  items: CountryItem[];
  totalItems: number;
  totalPages: number;
}

const initialState: CountriesState = {
  list: [],
  whiteList: [],
  blockedList: [],
  selectedType: "white",
  types: types,
  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,
  isDomainCountriesFetching: false,
  isDomainsFetching: false,
  domainCountryCodes: [],
  domainCountriesDomain: "",
  availableDomains: [],
  selectedDomain: "",
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
};

async function getCountries(
  type: CountryType,
  limit?: number,
  offset?: number,
  domain?: string,
) {
  return instance.get(apiRoutes.countriesList, {
    params: { type, limit, offset, domain },
  });
}

const getCountriesList = createAsyncThunk(
  "country/list",
  async (data: GetCountriesListPayload) => {
    try {
      const { type, limit, offset, domain } = data;
      const result = await getCountries(type, limit, offset, domain);

      return result.data as CountriesListResponse;
    } catch (error) {
      throw error;
    }
  },
);

const getWhiteCountriesList = createAsyncThunk(
  "country/whiteList",
  async () => {
    try {
      const result = await getCountries("white");

      return result.data as CountriesListResponse;
    } catch (error) {
      throw error;
    }
  },
);

const getBlockedCountriesList = createAsyncThunk(
  "country/blockedList",
  async () => {
    try {
      const result = await getCountries("blocked");

      return result.data as CountriesListResponse;
    } catch (error) {
      throw error;
    }
  },
);

const getDomainCountries = createAsyncThunk(
  "country/domainCountries",
  async ({ domain, type }: { domain: string; type: CountryType }) => {
    const result = await instance.get(apiRoutes.countriesList, {
      params: { domain, type, limit: 1000, offset: 0 },
    });
    const data = result.data ?? {};
    const values =
      data.items ??
      data.data?.items ??
      data[type] ??
      data.data?.[type] ??
      data[`${type}Countries`] ??
      data.items?.[type] ??
      data.countries?.[type] ??
      [];

    return (Array.isArray(values) ? values : []).map(
      (country: CountryItem | string) =>
        typeof country === "string" ? country : country.code,
    );
  },
);

const getAvailableMirrorDomains = createAsyncThunk(
  "country/availableDomains",
  async () => {
    const result = await instance.post(apiRoutes.mirrorList, {
      limit: 1000,
      offset: 0,
    });

    return (result.data?.items ?? [])
      .filter(
        (mirror: { status?: string }) =>
          mirror.status?.toLowerCase() !== "removed",
      )
      .map((mirror: { domain: string }) => mirror.domain);
  },
);

const updateCountries = createAsyncThunk(
  "country/update",
  async (data: UpdateCountriesPayload) => {
    try {
      await instance.put(`${apiRoutes.countriesUpdate}`, data);

      return true;
    } catch (error) {
      throw error;
    }
  },
);

const deleteCountry = createAsyncThunk(
  "country/delete",
  async (data: DeleteCountryPayload) => {
    try {
      await instance.delete(`${apiRoutes.countriesDelete}`, { data });

      return true;
    } catch (error) {
      throw error;
    }
  },
);

const removeCountryFromDomainList = createAsyncThunk(
  "country/removeFromDomainList",
  async ({
    domain,
    type,
    countryCode,
    totalItems,
  }: {
    domain: string;
    type: CountryType;
    countryCode: string;
    totalItems: number;
  }) => {
    const result = await getCountries(type, Math.max(totalItems, 1), 0, domain);
    const countries = (result.data?.items ?? []) as CountryItem[];

    await instance.put(apiRoutes.countriesUpdate, {
      domain,
      type,
      countries: countries
        .filter(({ code }) => code !== countryCode)
        .map(({ code }) => code),
    });

    return countryCode;
  },
);

const countrySlice = createSlice({
  name: "country",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },
    handleSelectType: (state, action: PayloadAction<CountryType>) => {
      state.selectedType = action.payload;
      state.isListFetched = false;
    },
    handleSelectDomain: (state, action: PayloadAction<string>) => {
      state.selectedDomain = action.payload;
      state.activePage = 1;
      state.list = [];
      state.isListFetched = !action.payload;
    },
    handleClear: (state: CountriesState) => {
      state.isItemFetched = false;
      state.isDomainCountriesFetching = false;
      state.domainCountryCodes = [];
    },
    clearDomainCountries: (state: CountriesState) => {
      state.domainCountryCodes = [];
      state.isDomainCountriesFetching = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCountriesList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getCountriesList.pending, (state) => {
      state.isListFetched = false;
      state.list = [];
    });
    builder.addCase(getCountriesList.rejected, (state) => {
      state.isListFetched = true;
    });
    builder.addCase(updateCountries.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(updateCountries.fulfilled, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateCountries.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(getWhiteCountriesList.fulfilled, (state, action) => {
      state.whiteList = action.payload?.items ?? [];
      state.isItemFetched = true;
    });
    builder.addCase(getBlockedCountriesList.fulfilled, (state, action) => {
      state.blockedList = action.payload?.items ?? [];
      state.isItemFetched = true;
    });
    builder.addCase(deleteCountry.pending, (state, action) => {
      state.processingItemId = action.meta.arg.countries[0];
    });
    builder.addCase(deleteCountry.fulfilled, (state) => {
      state.processingItemId = "";
      state.isListFetched = false;
    });
    builder.addCase(deleteCountry.rejected, (state) => {
      state.processingItemId = "";
    });
    builder.addCase(removeCountryFromDomainList.pending, (state, action) => {
      state.processingItemId = action.meta.arg.countryCode;
    });
    builder.addCase(removeCountryFromDomainList.fulfilled, (state) => {
      state.processingItemId = "";
    });
    builder.addCase(removeCountryFromDomainList.rejected, (state) => {
      state.processingItemId = "";
    });
    builder.addCase(getDomainCountries.pending, (state, action) => {
      state.isDomainCountriesFetching = true;
      state.domainCountryCodes = [];
      state.domainCountriesDomain = action.meta.arg.domain;
    });
    builder.addCase(getDomainCountries.fulfilled, (state, action) => {
      if (state.domainCountriesDomain !== action.meta.arg.domain) return;
      state.isDomainCountriesFetching = false;
      state.domainCountryCodes = action.payload;
    });
    builder.addCase(getDomainCountries.rejected, (state, action) => {
      if (state.domainCountriesDomain !== action.meta.arg.domain) return;
      state.isDomainCountriesFetching = false;
      state.domainCountryCodes = [];
    });
    builder.addCase(getAvailableMirrorDomains.pending, (state) => {
      state.isDomainsFetching = true;
    });
    builder.addCase(getAvailableMirrorDomains.fulfilled, (state, action) => {
      state.isDomainsFetching = false;
      state.availableDomains = action.payload;
    });
    builder.addCase(getAvailableMirrorDomains.rejected, (state) => {
      state.isDomainsFetching = false;
      state.availableDomains = [];
    });
  },
});

const {
  handlePaginate,
  handleSelectType,
  handleSelectDomain,
  handleClear,
  clearDomainCountries,
} = countrySlice.actions;

export {
  countrySlice,
  getCountriesList,
  getWhiteCountriesList,
  getBlockedCountriesList,
  updateCountries,
  deleteCountry,
  removeCountryFromDomainList,
  handlePaginate,
  handleSelectType,
  handleSelectDomain,
  handleClear,
  clearDomainCountries,
  getDomainCountries,
  getAvailableMirrorDomains,
};
